import { useState } from 'react'
import { Check, Copy, KeyRound, Mail, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Input, Modal, SegmentedControl, Toggle } from '@/components/ui'
import type { SegmentedControlOption } from '@/components/ui'
import type { User } from '@/types'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { newPasswordSchema } from '@/lib/passwordPolicy'
import { useAdminSetUserPassword, useTriggerUserPasswordReset } from '../hooks/useUsers'
import {
  adminPasswordResetConfirmMessage,
  generateTemporaryPassword,
} from '../utils/adminPasswordReset'

export type ResetPasswordMethod = 'email' | 'set'

export type ResetPasswordTarget = Pick<User, '_id' | 'email' | 'firstName' | 'lastName'>

export interface ResetPasswordDialogProps {
  user: ResetPasswordTarget | null
  onClose: () => void
}

const METHOD_OPTIONS: SegmentedControlOption<ResetPasswordMethod>[] = [
  { value: 'email', label: 'Email a link', icon: Mail },
  { value: 'set', label: 'Set password', icon: KeyRound },
]

function displayName(user: ResetPasswordTarget): string {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email
}

/**
 * One place for an admin to reset someone's password: email a single-use
 * link (preferred), or set a password directly when email cannot reach them.
 * A set password stays on screen after saving so it can be copied and shared.
 */
export function ResetPasswordDialog({
  user,
  onClose,
}: ResetPasswordDialogProps): React.ReactElement | null {
  if (!user) return null
  // Keyed by person: state starts fresh, so a password never carries across users.
  return <ResetPasswordDialogContent key={user._id} user={user} onClose={onClose} />
}

function ResetPasswordDialogContent({
  user,
  onClose,
}: {
  user: ResetPasswordTarget
  onClose: () => void
}): React.ReactElement {
  const [method, setMethod] = useState<ResetPasswordMethod>('email')
  const [password, setPassword] = useState(generateTemporaryPassword)
  const [mustChange, setMustChange] = useState(true)
  const [touched, setTouched] = useState(false)
  const [saved, setSaved] = useState<{ password: string; mustChange: boolean } | null>(null)
  const [copied, setCopied] = useState(false)

  const sendReset = useTriggerUserPasswordReset()
  const setPasswordMutation = useAdminSetUserPassword()

  const name = displayName(user)
  const validation = newPasswordSchema.safeParse(password)
  const passwordError = validation.success ? undefined : validation.error.issues[0]?.message
  const busy = sendReset.isPending || setPasswordMutation.isPending

  const copy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Could not copy. Select the password and copy it by hand.')
    }
  }

  const regenerate = (): void => {
    setPassword(generateTemporaryPassword())
    setTouched(false)
    setCopied(false)
  }

  const handleSendLink = (): void => {
    sendReset.mutate({ id: user._id }, { onSuccess: onClose })
  }

  const handleSetPassword = (): void => {
    setTouched(true)
    if (!validation.success) return
    setPasswordMutation.mutate(
      { id: user._id, newPassword: password, mustChangePassword: mustChange },
      {
        onSuccess: () => {
          setSaved({ password, mustChange })
          setCopied(false)
          toast.success(`Password set for ${name}`)
        },
      }
    )
  }

  const footer = saved ? (
    <div className="flex justify-end">
      <Button type="button" onClick={onClose}>
        Done
      </Button>
    </div>
  ) : (
    <div className="flex justify-end gap-3">
      <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      {method === 'email' ? (
        <Button type="button" onClick={handleSendLink} loading={sendReset.isPending}>
          Send link
        </Button>
      ) : (
        <Button
          type="button"
          onClick={handleSetPassword}
          loading={setPasswordMutation.isPending}
          disabled={touched && !validation.success}
        >
          Set password
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      title={saved ? 'Password set' : 'Reset password'}
      className="max-w-md"
      footer={footer}
    >
      {saved ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Give this password to{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{name}</span> privately,
            in person or by phone. It will not be shown again.
          </p>
          <PasswordReadout
            value={saved.password}
            copied={copied}
            onCopy={() => copy(saved.password)}
          />
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-gray-600 dark:text-gray-400">
            <li>They have been signed out of every device.</li>
            <li>
              {saved.mustChange
                ? 'They will be asked to choose their own password when they sign in.'
                : 'They can keep using this password.'}
            </li>
          </ul>
        </div>
      ) : (
        <div className="space-y-5">
          <SegmentedControl
            options={METHOD_OPTIONS}
            value={method}
            onChange={setMethod}
            ariaLabel="How to reset the password"
            className="w-full"
            disabled={busy}
          />

          {method === 'email' ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {adminPasswordResetConfirmMessage(name)}
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Use this when the email does not reach{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{name}</span>. You
                give them the password yourself. Saving signs them out of every device.
              </p>
              <div>
                <Input
                  label="New password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setTouched(true)
                    setCopied(false)
                  }}
                  onBlur={() => setTouched(true)}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono tracking-wide"
                  error={touched ? passwordError : undefined}
                  hint={touched && passwordError ? undefined : 'Generated for you. You can edit it.'}
                />
                <div className="mt-2 flex gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={regenerate}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Generate
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => copy(password)}
                    disabled={!password}
                  >
                    {copied ? (
                      <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" aria-hidden />
                    ) : (
                      <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
              <Toggle
                checked={mustChange}
                onChange={setMustChange}
                label="Ask them to choose a new password"
                description="Required at their next sign-in, so only they know their password."
                size="sm"
              />
              {setPasswordMutation.error ? (
                <p className="text-sm text-danger" role="alert">
                  {getErrorMessage(setPasswordMutation.error)}
                </p>
              ) : null}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function PasswordReadout({
  value,
  copied,
  onCopy,
}: {
  value: string
  copied: boolean
  onCopy: () => void
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2 pl-3.5 dark:border-gray-700 dark:bg-gray-900/60">
      <code className="min-w-0 flex-1 select-all break-all font-mono text-base tracking-wide text-gray-900 dark:text-gray-100">
        {value}
      </code>
      <Button type="button" variant="secondary" size="sm" onClick={onCopy} className="shrink-0">
        {copied ? (
          <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" aria-hidden />
        ) : (
          <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        )}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  )
}

ResetPasswordDialog.displayName = 'ResetPasswordDialog'
